// Time cards (rev. 1.12): one person's week as a calendar in US Central, each block colored by the
// code it counts toward, so it's plain where time was reported. Click a block to change it. A person
// may recode their own blocks or mark them not work time until the Tuesday cutoff; new time goes in
// Outlook, then a resync. An admin may also move, resize, add and remove blocks, at any time. Every
// change is kept with the week; the Changes section reports them by person.

import { useMemo, useState } from "react";
import { Badge, Body1, Button, Caption1, Card, Input, Select, Subtitle2, Switch } from "@fluentui/react-components";
import { addDays, FLAG_TEXT, HQ_TIME_ZONE, mondayOf, today, wallClock, type TimeChange, type TimeEntry } from "../../../core";
import { stamp } from "../format";
import { errorText, send, useApi, type PeopleNamesResponse, type SyncRunResponse } from "../api";
import { DateInput } from "../DateInput";
import { useCodeLabels } from "../codes";
import { ErrorBar, Loading, useMe, Warnings, ArchivedSwitch, useShowArchived } from "../ui";

interface TimecardResponse {
  person: { id: string; name: string };
  monday: string;
  state: "open" | "frozen";
  etag: string | null;
  entries: TimeEntry[];
  changes: TimeChange[];
  can: { recode: boolean; retime: boolean; add: boolean };
  /** The codes each day's blocks can take: projects booked that day inside their contract dates, and time off. */
  codesByDay?: Record<string, string[]>;
  own: boolean;
  /** Hours reported (not personal, not marked not work) and the share on billable projects. */
  summary?: { reported: number; billable: number; billablePct: number | null };
}

const HOUR_PX = 44;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const h1 = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 2 });
const dateLabel = (d: string) => d.slice(0, 10);
const hhmm = (min: number) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(Math.round(min % 60)).padStart(2, "0")}`;
const clock = (min: number) => {
  const hr = Math.floor(min / 60) % 24;
  const m = Math.round(min % 60);
  return `${hr % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""}${hr < 12 ? "am" : "pm"}`;
};

/** Minutes after midnight Central where an entry's part on its day starts and ends. */
function spanOf(e: TimeEntry): [number, number] {
  const start = wallClock(Date.parse(e.start));
  const from = start.day === e.day ? start.msOfDay / 60_000 : 0;
  return [from, Math.min(24 * 60, from + e.hours * 60)];
}

/** Side-by-side lanes for blocks that overlap on one day. */
function lanes(entries: TimeEntry[]): Map<string, { lane: number; of: number }> {
  const sorted = [...entries].sort((a, b) => spanOf(a)[0] - spanOf(b)[0]);
  const out = new Map<string, { lane: number; of: number }>();
  let group: TimeEntry[] = [];
  let groupEnd = -1;
  const ends: number[] = [];
  const flush = () => {
    const of = Math.max(1, ...group.map((e) => out.get(e.id)!.lane + 1));
    for (const e of group) out.set(e.id, { lane: out.get(e.id)!.lane, of });
    group = [];
    ends.length = 0;
  };
  for (const e of sorted) {
    const [s, end] = spanOf(e);
    if (group.length && s >= groupEnd) flush();
    let lane = ends.findIndex((x) => x <= s);
    if (lane < 0) lane = ends.length;
    ends[lane] = end;
    out.set(e.id, { lane, of: 1 });
    group.push(e);
    groupEnd = Math.max(groupEnd, end);
  }
  if (group.length) flush();
  return out;
}

export function TimeCards({ names }: { names: Map<string, string> }) {
  const me = useMe();
  const canPick = me.can("plans.read") || me.can("period.close");
  const people = useApi<PeopleNamesResponse>(canPick ? "/people?view=names" : null);
  const [personId, setPersonId] = useState("");
  const [monday, setMonday] = useState(mondayOf(today()));
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState<{ day: string; from: string; to: string } | null>(null);
  // An admin opens on the first active person; a member, on their own card.
  const firstActive = (people.data?.people ?? []).find((p) => p.status !== "archived")?.id ?? "";
  const [showArchived, setShowArchived] = useShowArchived("timecards");
  const pickable = (people.data?.people ?? []).filter((p) => showArchived || p.status !== "archived" || p.id === personId);
  const who = personId || (canPick ? firstActive : "");
  const waiting = canPick && !who;
  const card = useApi<TimecardResponse>(waiting ? null : `/actuals/timecard?monday=${monday}${who ? `&personId=${encodeURIComponent(who)}` : ""}`);
  const { label, color: colorOf, options } = useCodeLabels();
  const d = card.data;
  // Only a project booked that day, inside its contract dates, can take its hours (Chris, 2026-10-28).
  const optionsOn = (day: string) => (d?.codesByDay ? options.filter((o) => d.codesByDay![day]?.includes(o.code)) : options);
  const days = DAYS.map((_, i) => addDays(monday, i));
  const entry = d?.entries.find((e) => e.id === selected) ?? null;

  const timed = (d?.entries ?? []).filter((e) => e.hours > 0);
  const startHour = Math.min(7, ...timed.map((e) => Math.floor(spanOf(e)[0] / 60)));
  const endHour = Math.max(19, ...timed.map((e) => Math.ceil(spanOf(e)[1] / 60)));
  const laneOf = useMemo(() => {
    const out = new Map<string, { lane: number; of: number }>();
    for (const day of days) for (const [k, v] of lanes(timed.filter((e) => e.day === day))) out.set(k, v);
    return out;
  }, [d]);

  const [resyncing, setResyncing] = useState(false);
  const [resynced, setResynced] = useState<string>();
  const [resyncError, setResyncError] = useState<string>();
  async function resync() {
    if (!d) return;
    setResyncing(true);
    setResynced(undefined);
    setResyncError(undefined);
    try {
      // An open week resyncs with the person's other open weeks; an admin re-syncs a locked week by itself, taking Outlook's version.
      const locked = d.state === "frozen";
      const r = await send<SyncRunResponse>("POST", "/actuals/sync", { personId: d.person.id, ...(locked ? { from: d.monday, to: d.monday, locked: "apply" } : {}) });
      const p = r.people[0];
      if (!r.window) setResynced(`The calendar sync starts on ${r.cutover}; before that, hours come from the Timecard Tool import.`);
      else if (!p || p.status !== "ok") setResyncError(p?.status === "denied" ? `${d.person.name} isn't in the Timekeepers group, so the app can't read their calendar.` : "The calendar couldn't be read just now. Try again shortly.");
      else setResynced(`Resynced: ${p.added} added, ${p.updated} changed, ${p.removed} removed${p.flagged ? `, ${p.flagged} changes to frozen weeks sent to the inbox` : ""}.`);
      card.reload();
    } catch (err) {
      setResyncError(errorText(err));
    }
    setResyncing(false);
  }

  const codeOf = (e: TimeEntry) => e.resolution?.codes ?? e.codes;
  const byCode = new Map<string, number>();
  for (const e of timed) {
    const codes = codeOf(e);
    if (e.resolution && !codes.length) continue;
    for (const c of codes.length ? codes : [""]) byCode.set(c, (byCode.get(c) ?? 0) + e.hours / Math.max(1, codes.length));
  }

  return (
    <>
      <Card className="panel wide">
        <div className="toolbar">
          <Subtitle2>Time card</Subtitle2>
          {canPick && (
            <Select size="small" value={who} onChange={(_, x) => (setPersonId(x.value), setSelected(null))} aria-label="Person">
              {pickable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.status === "archived" ? " (archived)" : ""}
                </option>
              ))}
            </Select>
          )}
          {canPick && <ArchivedSwitch count={(people.data?.people ?? []).filter((p) => p.status === "archived").length} on={showArchived} onChange={setShowArchived} />}
          <Button size="small" onClick={() => (setMonday(addDays(monday, -7)), setSelected(null))} aria-label="Previous week">
            ‹
          </Button>
          <DateInput size="small" type="date" value={monday} onChange={(_, x) => x.value && (setMonday(mondayOf(x.value)), setSelected(null))} aria-label="Week of" />
          <Button size="small" onClick={() => (setMonday(addDays(monday, 7)), setSelected(null))} aria-label="Next week">
            ›
          </Button>
          <Caption1 className="muted">
            Week of {dateLabel(monday)}, US Central.{" "}
            {d && (d.state === "open" ? "Open until Tuesday's cutoff." : "Frozen: past its cutoff.")}
          </Caption1>
          {d?.can.add && (
            <Button size="small" appearance="primary" onClick={() => (setAdding({ day: monday, from: "09:00", to: "10:00" }), setSelected(null))}>
              Add a block
            </Button>
          )}
          {d?.summary && (
            <div className="week-summary" aria-label="This week">
              <span title="Hours on the card this week, leaving out personal time and blocks marked not work">
                <strong>{h1(d.summary.reported)}</strong> hours reported
              </span>
              <span title={`${h1(d.summary.billable)} h on projects marked billable, of ${h1(d.summary.reported)} h reported`}>
                <strong>{d.summary.billablePct === null ? "—" : `${Math.round(d.summary.billablePct * 100)}%`}</strong> billable
              </span>
            </div>
          )}
          {d && (me.can("period.close") || (d.own && d.state === "open")) && (
            <Button
              size="small"
              disabled={resyncing}
              onClick={() => void resync()}
              title={d.state === "frozen" ? "This week is locked. Re-read it from Outlook as it is now, keeping recodes and moved blocks; logged as an administrative change." : "Read this person's Outlook calendar now, rather than waiting for tonight's sync"}
            >
              {resyncing ? "Resyncing…" : d.state === "frozen" ? "Re-sync this locked week" : "Resync from Outlook"}
            </Button>
          )}
        </div>
        <ErrorBar message={card.error ?? people.error ?? resyncError} />
        {resynced && <Warnings intent="info" items={[resynced]} />}
        {d && d.own && !d.can.recode && <Warnings intent="info" items={["This week is past its cutoff, so only an admin can change it."]} />}
        {d && d.own && d.can.recode && (
          <Caption1 className="block">You can recode your blocks or mark them not work time until Tuesday's cutoff. To add or move time, change it in Outlook, then resync.</Caption1>
        )}
        {card.loading && !d ? (
          <Loading />
        ) : !d ? null : (
          <>
            <div className="week" style={{ gridTemplateRows: `auto ${(endHour - startHour) * HOUR_PX}px` }}>
              <div className="week-corner" />
              {days.map((day, i) => {
                const flags = d.entries.filter((e) => e.day === day && e.hours === 0);
                const total = timed.filter((e) => e.day === day && !(e.resolution && !e.resolution.codes.length)).reduce((t, e) => t + e.hours, 0);
                return (
                  <div key={day} className="week-head">
                    <strong>
                      {DAYS[i]} {dateLabel(day)}
                    </strong>
                    <Caption1 className="block muted">{total ? `${h1(total)} h` : " "}</Caption1>
                    {flags.map((f) => (
                      <button key={f.id} type="button" className="week-flag" onClick={() => setSelected(f.id)} title={f.flags.map((x) => FLAG_TEXT[x]).join("; ")}>
                        Out of office, no block
                      </button>
                    ))}
                  </div>
                );
              })}
              <div className="week-hours">
                {Array.from({ length: endHour - startHour }, (_, i) => (
                  <div key={i} className="week-hour" style={{ height: HOUR_PX }}>
                    {clock((startHour + i) * 60)}
                  </div>
                ))}
              </div>
              {days.map((day) => (
                <div
                  key={day}
                  className="week-day"
                  onDoubleClick={(e) => {
                    if (!d.can.add) return;
                    const y = e.clientY - (e.currentTarget as HTMLElement).getBoundingClientRect().top;
                    const min = Math.floor(((y / HOUR_PX) * 60 + startHour * 60) / 15) * 15;
                    setAdding({ day, from: hhmm(min), to: hhmm(Math.min(24 * 60, min + 60)) });
                    setSelected(null);
                  }}
                >
                  {Array.from({ length: endHour - startHour }, (_, i) => (
                    <div key={i} className="week-line" style={{ top: i * HOUR_PX }} />
                  ))}
                  {timed
                    .filter((e) => e.day === day)
                    .map((e) => {
                      const [s, end] = spanOf(e);
                      const codes = codeOf(e);
                      const notWork = !!e.resolution && !codes.length;
                      const { fill, edge } = codes.length ? colorOf(codes[0]) : { fill: "var(--mist)", edge: "var(--rain)" };
                      const { lane, of } = laneOf.get(e.id) ?? { lane: 0, of: 1 };
                      const needs = e.flags.length > 0 && !e.resolution;
                      return (
                        <button
                          key={e.id}
                          type="button"
                          className={`week-block${notWork ? " not-work" : ""}${needs ? " needs" : ""}${selected === e.id ? " chosen" : ""}`}
                          style={{
                            top: (s / 60 - startHour) * HOUR_PX,
                            height: Math.max(18, ((end - s) / 60) * HOUR_PX - 2),
                            left: `calc(${(lane / of) * 100}% + 2px)`,
                            width: `calc(${100 / of}% - 4px)`,
                            background: fill,
                            borderColor: edge,
                          }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setSelected(e.id);
                            setAdding(null);
                          }}
                          title={`${clock(s)}–${clock(end)} · ${codes.map(label).join(", ") || (notWork ? "Not work time" : "No code")}${e.subject ? ` · ${e.subject}` : ""}`}
                        >
                          <span className="week-block-code">{notWork ? "Not work time" : codes.join(", ") || "No code"}</span>
                          <span className="week-block-text">
                            {clock(s)}–{clock(end)} {e.subject}
                          </span>
                          {(e.retimed || e.source === "manual") && <span className="week-block-mark">edited</span>}
                        </button>
                      );
                    })}
                </div>
              ))}
            </div>
            {byCode.size > 0 && (
              <div className="week-legend">
                {[...byCode.entries()]
                  .sort(([, a], [, b]) => b - a)
                  .map(([code, hours]) => (
                    <span key={code} className="week-key">
                      <span className="week-swatch" style={code ? { background: colorOf(code).fill, borderColor: colorOf(code).edge } : undefined} />
                      {code ? label(code) : "No code"}: {h1(hours)} h
                    </span>
                  ))}
              </div>
            )}
            {d.can.add && <Caption1 className="muted">Double-click an empty spot to add a block there.</Caption1>}
          </>
        )}
      </Card>

      {d && entry && <BlockEditor key={entry.id + (d.etag ?? "")} card={d} entry={entry} options={optionsOn(entry.day)} label={label} onDone={() => (card.reload(), setSelected(null))} />}
      {d && adding && <AddBlock key={`${adding.day}${adding.from}`} card={d} start={adding} optionsOn={optionsOn} onDone={() => (card.reload(), setAdding(null))} onCancel={() => setAdding(null)} />}

      {d && d.changes.length > 0 && (
        <Card className="panel wide">
          <Subtitle2>Changes this week</Subtitle2>
          <ul className="plain-list">
            {[...d.changes].reverse().map((c, i) => (
              <li key={i}>
                <Caption1>
                  {stamp(c.at, HQ_TIME_ZONE)} · {c.by}
                  {c.administrative ? " (admin)" : ""}: {c.summary}
                  {c.note ? ` — ${c.note}` : ""}
                </Caption1>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function BlockEditor({ card, entry, options, label, onDone }: { card: TimecardResponse; entry: TimeEntry; options: { code: string; label: string }[]; label: (c: string) => string; onDone: () => void }) {
  const [s, end] = spanOf(entry);
  const current = entry.resolution?.codes ?? entry.codes;
  const [code, setCode] = useState(current[0] ?? "");
  const pickable = options.some((o) => o.code === current[0]);
  const [day, setDay] = useState(entry.day);
  const [from, setFrom] = useState(hhmm(s));
  const [to, setTo] = useState(hhmm(end));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const days = DAYS.map((_, i) => addDays(card.monday, i));

  async function act(action: string, extra: Record<string, unknown> = {}) {
    setBusy(true);
    setError(undefined);
    try {
      await send("POST", "/actuals/adjust", { personId: card.person.id, monday: card.monday, etag: card.etag, action, entryId: entry.id, note, ...extra });
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const changed = !!entry.resolution || !!entry.retimed;
  return (
    <Card className="panel wide">
      <Subtitle2>
        {dateLabel(entry.day)}
        {entry.hours > 0 ? `, ${clock(s)}–${clock(end)} (${h1(entry.hours)} h)` : ", all day"}
      </Subtitle2>
      {entry.subject && <Body1>{entry.subject}</Body1>}
      <Caption1 className="block">
        Category in Outlook: {entry.categories.join(", ") || "none"} · Counts toward: {current.map(label).join(", ") || (entry.resolution ? "not work time" : "no code yet")}
      </Caption1>
      {entry.flags.map((f) => (
        <Badge key={f} appearance="tint" color={entry.resolution ? "informative" : "warning"} size="small" className="in-effect">
          {FLAG_TEXT[f]}
        </Badge>
      ))}
      {entry.retimed && (
        <Caption1 className="block muted">
          Moved by {entry.retimed.by}; the calendar had {clock(spanOf({ ...entry, start: entry.retimed.start, hours: entry.retimed.hours })[0])}, {h1(entry.retimed.hours)} h.
        </Caption1>
      )}
      <ErrorBar message={error} />
      {card.can.recode ? (
        <>
          <div className="toolbar">
            <Select size="small" value={code} onChange={(_, x) => setCode(x.value)} aria-label="Code">
              <option value="">Pick a code</option>
              {current[0] && !pickable && (
                <option value={current[0]} disabled>
                  {label(current[0])} (no chargeline this day)
                </option>
              )}
              {options.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Input size="small" placeholder="Note (optional)" value={note} onChange={(_, x) => setNote(x.value)} aria-label="Note" />
            <Button size="small" appearance="primary" disabled={busy || !code || !options.some((o) => o.code === code) || entry.hours === 0} onClick={() => void act("recode", { codes: [code] })}>
              Recode
            </Button>
            {card.can.retime && entry.flags.length > 0 && !entry.resolution && entry.codes.length > 0 && (
              <Button size="small" disabled={busy} onClick={() => void act("approve")} title="Accept as reported: every code counts">
                Approve
              </Button>
            )}
            <Button size="small" disabled={busy} onClick={() => void act("not-work")}>
              {entry.hours === 0 ? "Dismiss" : "Not work time"}
            </Button>
            {changed && entry.source !== "manual" && (
              <Button size="small" appearance="subtle" disabled={busy} onClick={() => void act("reset")}>
                Back to the calendar
              </Button>
            )}
          </div>
          {card.can.retime && entry.hours > 0 && (
            <div className="toolbar">
              <Caption1>Move or resize</Caption1>
              <Select size="small" value={day} onChange={(_, x) => setDay(x.value)} aria-label="Day">
                {days.map((x, i) => (
                  <option key={x} value={x}>
                    {DAYS[i]} {dateLabel(x)}
                  </option>
                ))}
              </Select>
              <Input size="small" type="time" step={900} value={from} onChange={(_, x) => setFrom(x.value)} aria-label="Start" />
              <Input size="small" type="time" step={900} value={to} onChange={(_, x) => setTo(x.value)} aria-label="End" />
              <Button size="small" disabled={busy} onClick={() => void act("retime", { day, from, to })}>
                Save times
              </Button>
              {entry.source === "manual" && (
                <Button size="small" appearance="subtle" disabled={busy} onClick={() => void act("remove")}>
                  Remove this block
                </Button>
              )}
            </div>
          )}
        </>
      ) : (
        <Caption1 className="muted">You can look but not change this time card.</Caption1>
      )}
    </Card>
  );
}

function AddBlock({ card, start, optionsOn, onDone, onCancel }: { card: TimecardResponse; start: { day: string; from: string; to: string }; optionsOn: (day: string) => { code: string; label: string }[]; onDone: () => void; onCancel: () => void }) {
  const [day, setDay] = useState(start.day);
  const options = optionsOn(day);
  const [from, setFrom] = useState(start.from);
  const [to, setTo] = useState(start.to);
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const days = DAYS.map((_, i) => addDays(card.monday, i));
  async function add() {
    setBusy(true);
    setError(undefined);
    try {
      await send("POST", "/actuals/adjust", { personId: card.person.id, monday: card.monday, etag: card.etag, action: "add", day, from, to, codes: [code], note });
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }
  return (
    <Card className="panel wide">
      <Subtitle2>Add a block for {card.person.name}</Subtitle2>
      <Caption1>Time that isn't on their calendar. It's logged as an administrative change.</Caption1>
      <ErrorBar message={error} />
      <div className="toolbar">
        <Select size="small" value={day} onChange={(_, x) => setDay(x.value)} aria-label="Day">
          {days.map((x, i) => (
            <option key={x} value={x}>
              {DAYS[i]} {dateLabel(x)}
            </option>
          ))}
        </Select>
        <Input size="small" type="time" step={900} value={from} onChange={(_, x) => setFrom(x.value)} aria-label="Start" />
        <Input size="small" type="time" step={900} value={to} onChange={(_, x) => setTo(x.value)} aria-label="End" />
        <Select size="small" value={code} onChange={(_, x) => setCode(x.value)} aria-label="Code">
          <option value="">Pick a code</option>
          {options.map((o) => (
            <option key={o.code} value={o.code}>
              {o.label}
            </option>
          ))}
        </Select>
        <Input size="small" placeholder="What it was (optional)" value={note} onChange={(_, x) => setNote(x.value)} aria-label="What it was" />
        <Button size="small" appearance="primary" disabled={busy || !code || !options.some((o) => o.code === code)} onClick={() => void add()}>
          Add
        </Button>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

/** Changes made to time cards and in the inbox: by default only administrative ones (rev. 1.12). */
export function ChangesReport({ names }: { names: Map<string, string> }) {
  const [from, setFrom] = useState(addDays(today(), -30));
  const [to, setTo] = useState(today());
  const [personId, setPersonId] = useState("");
  const [onlyAdmin, setOnlyAdmin] = useState(true);
  const report = useApi<{ changes: (TimeChange & { personId: string; monday: string })[] }>(
    `/actuals/changes?from=${from}&to=${to}${personId ? `&personId=${encodeURIComponent(personId)}` : ""}${onlyAdmin ? "&administrative=1" : ""}`,
  );
  const rows = report.data?.changes ?? [];
  const byPerson = new Map<string, number>();
  for (const r of rows) byPerson.set(r.personId, (byPerson.get(r.personId) ?? 0) + 1);
  return (
    <Card className="panel wide">
      <div className="toolbar">
        <Subtitle2>Changes</Subtitle2>
        <label className="inline-field">
          <Caption1>Made from</Caption1>
          <DateInput size="small" type="date" value={from} onChange={(_, x) => x.value && setFrom(x.value)} aria-label="Made from" />
        </label>
        <label className="inline-field">
          <Caption1>to</Caption1>
          <DateInput size="small" type="date" value={to} onChange={(_, x) => x.value && setTo(x.value)} aria-label="Made to" />
        </label>
        <Select size="small" value={personId} onChange={(_, x) => setPersonId(x.value)} aria-label="Person">
          <option value="">Everyone</option>
          {[...names.entries()].sort(([, a], [, b]) => a.localeCompare(b)).map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Select>
        <Switch label="Administrative only" checked={onlyAdmin} onChange={(_, x) => setOnlyAdmin(x.checked)} />
      </div>
      <Caption1>
        Administrative: made by someone other than the person, or after the week's cutoff, on a time card or in the inbox.
      </Caption1>
      <ErrorBar message={report.error} />
      {report.loading ? (
        <Loading />
      ) : !rows.length ? (
        <Body1>No changes in this range.</Body1>
      ) : (
        <>
          <Caption1 className="block">
            {[...byPerson.entries()].map(([p, n]) => `${names.get(p) ?? p}: ${n}`).join(" · ")}
          </Caption1>
          <div className="table-scroll">
            <table className="edit-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Time card of</th>
                  <th>By</th>
                  <th>Change</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{stamp(r.at, HQ_TIME_ZONE)}</td>
                    <td>{names.get(r.personId) ?? r.personId}</td>
                    <td>
                      {r.by}
                      {r.administrative ? "" : " (self)"}
                    </td>
                    <td>{r.summary}</td>
                    <td>{r.note ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}
